import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ConsentFormItem, dataForConsentForms} from "./datasource";
import SignatureCanvas from "react-signature-canvas"
import {ActionContext, ActionType} from "../components/hooks/provider";
import {Appointment, Page, PatientInfo} from "./types";
import {LangManager} from "../components/i18n/langManager";
import { checkin, savePatientSignature } from './service'
import BridgeHelper, { openInApp } from "../bridge/bridgeHelper";
import { PromptContext } from "../utils/promptContext";
import { useParams } from 'react-router-dom'

interface IProps {
    patientInfo: PatientInfo,
    appointment: Appointment,
}

export default function ConsentForms({patientInfo, appointment}: IProps) {
    const [dataSource, setDataSource] = useState(dataForConsentForms)
    const [activeIndex, setActiveIndex] = useState(0)
    const signaturePadRef = useRef<SignatureCanvas | null>(null)
    const {dispatchActionState, actionState} = useContext(ActionContext)

    const {showSpinner, hideSpinner} = useContext(PromptContext)

    const {token} = useParams()

    useEffect(() => {
        let index = 0;
        for (let i = 0; i < dataSource.length; i ++) {
            if (dataSource[i].checked) {
                index = i + 1
            } else {
                break
            }
        }
        setActiveIndex(index)
    }, [dataSource])

    const langManager = LangManager(actionState.language)
    const $title = (
        <p className={"text-black text-base font-medium leading-5"}>{`${langManager.onlineCheckin} ${patientInfo.name}`}</p>
    )

    const $description = (
        <div className={"w-full h-max bg-[#EEF3F9] flex flex-col space-y-2 mt-3"}>
            <div className={"w-full"}>
                <span className={"text-base text-[#365496]"}>{langManager.steps(3)} </span><span className={"text-base text-black"}>{langManager.consentForms}</span>
            </div>
            <p className={"text-sm text-black"}>{langManager.consentFormsDesc}</p>
        </div>
    )

    const $healthixFormDescription = (
        <div className={"text-[#606060] text-xs"}>
            <p>
                {"I request that health inform action regarding my care and treatment be accessed as set forth on this form. I can choose whether or not to allow\n" +
                "                MDLand Demo Clinic to obtain access to my medical records through the health information exchange organization called Healthix. If I give consent, my medical records from different places where I get health care can be accessed using a statewide computer network. Healthix is a not-for-profit organization that shares information about people's health electronically and meets the privacy and security standards of HIPAA and New York State Law. To learn more visit Healthix's website at www.healthix.org."}
            </p>
            <p>
                {"If I want to deny consent for all Provider Organizations and Health Plans participating in Healthix to access my electronic health information through Healthix, I may do so by visiting Healthix's website at www.healthix.org or calling Healthix at 877-695-4749."}
            </p>
            <p>
                My questions about this form have been answered and I have been provided a copy of this form.
            </p>
            <p>
                Details about the information accessed through Healthix and the consent process:
            </p>
            <div>
                <p>{"1. How Your Information May be Used. Your electronic health information will be used only for the following healthcare services:"}</p>
                <ul>
                    <li>
                        ∙ Treatment Services. Provide you with medical treatment and related services.
                    </li>
                    <li>
                        ∙ Insurance Eligibility Verification. Check whether you have health insurance and what it covers.
                    </li>
                    <li>
                        ∙ Care Management Activities. These include assisting you in obtaining appropriate medical care, improving the quality of services provided to you, coordinating the provision of multiple health care service provided to you, or supporting you in following a plan of medical care.
                    </li>
                    <li>
                        ∙ Quality Improvement Activities. Evaluate and improve the quality of medical care provided to you and all patients.
                    </li>
                </ul>
            </div>
        </div>
    )

    const $patientAgreementFormDescription = (
        <div className={"text-[#606060] text-xs"}>
            <ul>
                <li>
                    ∙ Acknowledgement of HIPAA Notice of Privacy Practices
                </li>
                <li>
                    I hereby acknowledge that I have fully reviewed and/or have received a complete copy of the HIPAA notice of privacy practices provided by the staff of this office.
                </li>
                <li>
                    ∙ Medicare/Medicaid Assignment of Benefits
                </li>
                <li>
                    I certify that the information given by me in applying for payment is correct. I authorize release of all records upon request. I request that payment of authorized benefits be made on my behalf.
                </li>
                <li>
                    ∙ Assignment of Insurance Benefit
                </li>
                <li>
                    I hereby authorize direct payment of medical benefits to MDLand Clinic for services rendered by all medical providers in the corporation. I
                </li>
            </ul>
        </div>
    )

    const onUpdateCheckedStatus = (index: number) => {
        dataSource[index].checked = !dataSource[index].checked
        if (dataSource[index].checked && index < dataSource.length - 1 ) {
            dataSource[index + 1].open = true
        }
        const list = dataSource.map((_item) => {
            return _item
        })
        setDataSource(list)
    }

    const $getCheckedButton = (checked: boolean) => {
        return checked ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.3403 2H7.6703C4.2803 2 2.0003 4.38 2.0003 7.92V16.09C2.0003 19.62 4.2803 22 7.6703 22H16.3403C19.7303 22 22.0003 19.62 22.0003 16.09V7.92C22.0003 4.38 19.7303 2 16.3403 2" stroke="#365496" strokeWidth="1.5"/>
            <path d="M10.8134 15.248C10.5894 15.248 10.3654 15.163 10.1944 14.992L7.8214 12.619C7.4794 12.277 7.4794 11.723 7.8214 11.382C8.1634 11.04 8.7164 11.039 9.0584 11.381L10.8134 13.136L14.9414 9.00796C15.2834 8.66596 15.8364 8.66596 16.1784 9.00796C16.5204 9.34996 16.5204 9.90396 16.1784 10.246L11.4324 14.992C11.2614 15.163 11.0374 15.248 10.8134 15.248Z" fill="#365496"/>
        </svg> : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.3403 1.9998H7.6703C4.2803 1.9998 2.0003 4.3798 2.0003 7.9198V16.0898C2.0003 19.6198 4.2803 21.9998 7.6703 21.9998H16.3403C19.7303 21.9998 22.0003 19.6198 22.0003 16.0898V7.9198C22.0003 4.3798 19.7303 1.9998 16.3403 1.9998" stroke="#8698C0" strokeWidth="1.5"/>
        </svg>
    }

    const $checkedContent = (checked: boolean, content: string, onCheck: () => void) => {
        let s = content
        if (s.indexOf("ClinicName") !== -1) {
            s = s.replace("ClinicName", appointment.clinicName)
        }
        return (
            <div className={"w-full flex flex-row space-x-2"} onClick={(e) => {
                e.stopPropagation()
                onCheck()
            }}>
                <div className={"w-6 h-6 flex flex-none"}>
                    {$getCheckedButton(checked)}
                </div>
                <p className={"text-base leading-5 mt-0.5"}>{s}</p>
            </div>
        )
    }

    const onUpdateDiscloseStatus = (index: number) => {
        dataSource[index].open = !dataSource[index].open
        const list = dataSource.map((_item) => {
            return _item
        })
        setDataSource(list)
    }

    const $bottomCheckButton = (activeIndex: number) => {
        if (activeIndex === dataSource.length) {
            return null
        }
        return (
            <div className={"w-full fixed left-0 right-0 bottom-0 h-max h-24 bg-[#EEF3F9] px-4 flex items-center justify-center"}>
                {$checkedContent(dataSource[activeIndex].checked, dataSource[activeIndex].checkedContent, () => {
                    onUpdateCheckedStatus(activeIndex)
                })}
            </div>
        )
    }

    const $line = (
        <div className={"w-full h-px bg-[#E5E5E5]"}/>
    )

    const $discloseItem = (item: ConsentFormItem, idx: number, description: () => any) => {
        return (
            <div key={idx} className={"w-full"}>
                <div onClick={(e) => {
                    e.stopPropagation()
                    onUpdateDiscloseStatus(idx)
                }} className={"w-full flex flex-row items-center justify-between py-4"}>
                    <p className={"text-base font-bold"}>{item.title}</p>
                    <div
                        className={`${
                            item.open ? "" : "rotate-180"
                        }`}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 15.5L12 8.5L19 15.5" stroke="#365496" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                </div>
                {item.open ? <div className={"w-full flex flex-col space-y-6"}>
                    {description()}
                    {$checkedContent(item.checked, item.checkedContent, () => {
                        onUpdateCheckedStatus(idx)
                    })}
                    {$line}
                </div> : $checkedContent(item.checked, item.checkedContent, () => {
                    if (item.open) {
                        onUpdateCheckedStatus(idx)
                    } else {
                        onUpdateDiscloseStatus(idx)
                    }
                })}
            </div>
        )
    }

    const getSignatureData = () => {
        if (signaturePadRef && signaturePadRef.current) {
            return signaturePadRef.current?.toDataURL() || ""
        }
        return ""
    }

    const onClearSignature = () => {
        signaturePadRef && signaturePadRef.current?.clear()
    }

    const $signaturePad = (
        <div className={"w-full mt-6"}>
            <p className={"text-black text-base font-medium leading-5"}>{langManager.signature}</p>
            <div className={"w-full flex flex-row items-center justify-end"}>
                <div onClick={onClearSignature} className={"text-xs text-[#365496] leading-4 underline decoration-solid"}>
                    <span>{langManager.clear}</span>
                </div>
            </div>
            <SignatureCanvas ref={signaturePadRef} penColor="black"
                canvasProps={{className: "w-full border rounded my-2.5 h-48"}} />
            <p className={"text-sm leading-5"}>
                {langManager.signatureDesc}
            </p>

        </div>
    )

    const onCheckIn = (complete: (successful: boolean) => void) => {
        token && checkin(token, complete)
    }

    const saveSignature = () => {
        if (signaturePadRef.current?.isEmpty()) {
            alert("Please complete your signature")
            return
        }

        const signature = getSignatureData().split(",")
            .pop()!

        showSpinner("savePatientSignature")
        savePatientSignature(patientInfo, signature, (isSuccess) => {
            hideSpinner("savePatientSignature")
            if (isSuccess) {
                onCheckIn((successful) => {
                    if (successful) {
                        if (openInApp) {
                            BridgeHelper.popPage()
                        } else {
                            dispatchActionState({type: ActionType.page, value: Page.checkIn})
                        }
                    } else {
                        alert("Check-in failed")
                    }
                })
            } else {
                alert("Update failed.")
            }
        })
    }

    const $buttons = (
        <div className={"w-full grid grid-cols-2 space-x-5 mt-6"}>
            <button onClick={() => {
                dispatchActionState({type: ActionType.page, value: Page.profile})
            }} type={"button"} className={"py-3 px-2 text-base text-primary font-medium bg-white border-2 border-primary rounded-xl"}>
                {langManager.back}
            </button>

            <button onClick={saveSignature} type={"button"} className={"py-3 px-2 text-base text-white font-medium bg-primary rounded-xl truncate"}>
                {langManager.checkIn}
            </button>
        </div>
    )

    const checkedAllConsentForms = useMemo(() => {
        for (let i = 0; i < dataSource.length; i ++) {
            if (!dataSource[i].checked) {
                return false
            }
        }
        return true
    }, [dataSource])

    return (
        <div className={"w-full"}>
            <div className={"w-full px-5 pt-4 pb-28"}>
                {$title}
                {$description}
                {dataSource.slice(0, activeIndex + 1).map((item, index) => {
                    return ($discloseItem(item, index, () => {
                        return index === 0 ? $healthixFormDescription : $patientAgreementFormDescription
                    }))
                })}
                {checkedAllConsentForms ? $signaturePad : null}
                {checkedAllConsentForms ? $buttons : null}
                {$bottomCheckButton(activeIndex)}
            </div>
        </div>
    )
}
